<script setup lang="ts">
/**
 * Affiche une suite de touches clavier (raccourci).
 * Chaque touche s'anime individuellement (pressedKeys[i]).
 * Quand toutes sont enfoncées ensemble, effet sublimation (sublimation).
 */
withDefaults(
  defineProps<{
    /** Touches à afficher, ex. ['⌘', '⇧', 'E'] */
    keys: string[]
    /** Pour chaque touche, true = enfoncée (animation individuelle) */
    pressedKeys?: boolean[]
    /** true = effet sublimation (raccourci complet déclenché) */
    sublimation?: boolean
  }>(),
  { pressedKeys: () => [], sublimation: false }
)
</script>

<template>
  <span
    class="source-kbd"
    :class="{ 'source-kbd--sublimation': sublimation }"
  >
    <kbd
      v-for="(key, i) in keys"
      :key="i"
      class="source-kbd__key"
      :class="{ 'source-kbd__key--pressed': pressedKeys[i] }"
    >
      {{ key }}
    </kbd>
  </span>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/index" as *;
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/spacing" as *;

// Style inspiré de https://dylanatsmith.com/wrote/styling-the-kbd-element
// Ombre nette (sans blur) pour un rendu "clavier physique"

.source-kbd {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  font-size: 0.75rem;
  line-height: 1;

  &--sublimation .source-kbd__key {
    animation: kbd-shortcut-complete 1.2s ease;
  }
}

@keyframes kbd-shortcut-complete {
  0% {
    transform: translateY(0);
    box-shadow: 0 2px 0 1px c.$enedis-neutral-80;
    background: var(--color-bg-elevated);
    color: c.$enedis-neutral-700;
  }
  12% {
    transform: translateY(2px);
    box-shadow: 0 0 0 1px c.$enedis-primary-400;
    background: c.$enedis-primary-300;
    color: c.$enedis-primary-700;
  }
  15%,
  55% {
    transform: translateY(2px);
    box-shadow: 0 0 0 1px c.$enedis-primary-500;
    background: c.$enedis-primary-400;
    color: c.$enedis-neutral-10;
  }
  70% {
    transform: translateY(2px);
    box-shadow: 0 0 0 1px c.$enedis-primary-400;
    background: c.$enedis-primary-200;
    color: c.$enedis-primary-600;
  }
  100% {
    transform: translateY(0);
    box-shadow: 0 2px 0 1px c.$enedis-neutral-80;
    background: var(--color-bg-elevated);
    color: c.$enedis-neutral-700;
  }
}

.source-kbd__key {
  --kbd-bg: var(--color-bg-elevated);
  --kbd-border: #{c.$enedis-neutral-80};
  --kbd-text: #{c.$enedis-neutral-700};

  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0.75rem;
  padding: 2px $spacing-3xs;
  background: var(--kbd-bg);
  color: var(--kbd-text);
  border: 1px solid var(--kbd-border);
  border-radius: 0.25rem;
  /* Ombre nette type clavier physique (sans blur) */
  box-shadow: 0 2px 0 1px var(--kbd-border);
  font-weight: 500;
  white-space: nowrap;
  line-height: 1;
  text-align: center;
  position: relative;
  top: -1px;
  cursor: default;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.1s ease, color 0.1s ease;

  &:active {
    top: 1px;
    transform: translateY(2px);
    box-shadow: 0 0 0 1px var(--kbd-border);
    background: c.$enedis-neutral-30;
  }

  &--pressed {
    top: 1px;
    transform: translateY(2px);
    box-shadow: 0 0 0 1px var(--kbd-border);
    background: c.$enedis-neutral-30;
  }
}
</style>
